import { Injectable } from '@angular/core';
import { WindowService } from '@studiohyperdrive/ngx-utils';
import { ObservableNumber } from '@studiohyperdrive/rxjs-utils';
import { EMPTY, Observable, debounceTime, fromEvent, take, tap } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class BrowserService {
	constructor(private readonly windowService: WindowService) {}

	/**
	 * Get the current scroll position
	 */
	public getCurrentScrollPosition(): number {
		// Iben: Early exit if we're not in the browser
		if (!this.isBrowser) {
			return;
		}

		// Iben: Return the scroll position
		return this.windowService.window.scrollY;
	}

	/**
	 * Scroll to the provided position
	 *
	 * @param offset - The provided position to scroll to relative from the top
	 */
	public scrollToPosition(offset: number) {
		// Iben: Early exit if we're not in the browser
		if (this.isBrowser) {
			return;
		}

		// Iben: Scroll to the position
		this.windowService.window.scrollTo({ top: offset });
	}

	/**
	 * Scroll to the top of the page
	 */
	public scrollToTop() {
		// Iben: Early exit if we're not in the browser
		if (!this.isBrowser) {
			return;
		}

		// Iben: Scroll to the top
		this.windowService.scrollTo(0);
	}

	public scrollToElement(
		selector: string,
		alignCenter: boolean = true,
		behavior: ScrollBehavior = 'smooth'
	): Observable<Event> {
		if (!this.isBrowser) {
			return EMPTY;
		}

		// Abdurrahman:
		// Inspired from: https://medium.com/javascript-everyday/how-to-scroll-to-first-invalid-control-once-a-form-has-been-submitted-eb47d9fbc6e
		const firstElement: HTMLElement = this.document.querySelectorAll(selector)[0] as HTMLElement;
		const menu: HTMLElement = this.document.querySelector('.topbar') as HTMLElement;

		if (!firstElement) {
			return EMPTY;
		}

		// Denis: Calculate the offset to center the element in the viewport
		const boundingRect = firstElement.getBoundingClientRect();
		const absoluteElementTop = boundingRect.top + this.windowService.window.scrollY;
		const center = absoluteElementTop - this.windowService.window.innerHeight / 2;
		const menuOffset = absoluteElementTop - menu.offsetHeight - 30;

		this.windowService.window.scrollTo({
			top: alignCenter ? center : menuOffset,
			left: 0,
			behavior: behavior
		});

		return fromEvent(this.windowService.window, 'scroll').pipe(
			debounceTime(100),
			take(1),
			tap(() => firstElement.focus())
		);
	}

	/**
	 * Scroll to the element with the provided selector using scrollIntoView.
	 *
	 * @param selector - The selector of the element that needs to be scrolled to.
	 */
	public scrollIntoView(selector: string): void {
		if (!this.isBrowser) {
			return;
		}
		const firstElement: HTMLElement = this.document.querySelectorAll(selector)[0] as HTMLElement;

		if (this.isBrowser && firstElement) {
			firstElement.scrollIntoView({ behavior: 'smooth' });
			firstElement.focus();
		}
	}

	/**
	 * Scroll to the element with the provided id.
	 *
	 * @param id - The id that needs to be scrolled to. The element that needs to be scrolled to, requires
	 * a set tabindex.
	 */
	public scrollToId(id: string): void {
		const element = this.document.getElementById(id);

		if (this.isBrowser && element) {
			element.scrollIntoView({ behavior: 'smooth' });
			element.focus();
		}
	}

	/**
	 * Focus the provided element
	 *
	 * @param element - The native element that needs to be focused
	 */
	public focusOnElement(element: HTMLElement): void {
		if (this.isBrowser && element) {
			element.focus();
		}
	}

	/**
	 * Run a provided function only when we're in the browser and not in a server side rendered application
	 *
	 * @param action - Function we want to run in the browser
	 */
	public runInBrowser(action: (data: { browserWindow: Window; browserDocument: Document }) => void) {
		if (this.isBrowser) {
			action({ browserWindow: this.window, browserDocument: this.document });
		} else {
			console.warn('Browser depended function has not run.');
		}
	}

	/**
	 * Whether we're in the browser or on the server
	 */
	public get isBrowser(): boolean {
		return this.windowService.isBrowser();
	}

	/**
	 * Returns the window object
	 */
	private get window() {
		return this.windowService.window;
	}

	/**
	 * Returns the document object
	 */
	private get document() {
		return this.windowService.document;
	}

	/**
	 * Return the width of the browser window
	 */
	public get width$(): ObservableNumber {
		return this.windowService.width$;
	}

	/**
	 * Set the window.location.href property to navigate away from the app.
	 */
	public setWindowLocation(url: string): void {
		if (!this.isBrowser) {
			return;
		}

		this.window.location.href = url;
	}

	/**
	 * Get the window.location.href property to get the current URL.
	 */
	public getWindowLocation(): string {
		if (!this.isBrowser) {
			return;
		}

		return this.window.location.href;
	}

	/**
	 * Get the current url (without protocol and hostname)
	 */
	public getCurrentUrl(): string {
		if (!this.isBrowser) {
			return;
		}

		return `${this.window.location.pathname}${this.window.location.search}`;
	}

	/**
	 * Refresh using the window.location
	 */
	public refresh(): void {
		if (!this.isBrowser) {
			return;
		}

		this.window.location.reload();
	}
}
